import { FC, useContext } from "react";
import { GBP_SYMBOL } from "lib/constants";
import FlexBox from "../flex-box";
import SimpleCard from "../simple-card";
import SimpleButton from "../simple-button";
import { formatDate, toFormattedDate, formatDateUTCwithoutDay, toFormattedDateUTC } from "lib/utils/date-utils";
import { AppContext } from "lib/contexts/app-context";

interface PatientSessionBookingSummaryTileProps {
  id: string;
  clinicName: string;
  patientName: string;
  therapyName: string;
  duration: number;
  totalCost: string;
  invoiceURL?: string;
  status?: string;
  fromTime: string;
  onChangeSessionBookingStatus?: (bookingSessionId: string, status?: string) => void;
  onEditSessionBooking?: (bookingSessionId: string) => void;
}

const PatientSessionBookingSummaryTile: FC<PatientSessionBookingSummaryTileProps> = ({
  id,
  clinicName,
  therapyName,
  patientName,
  duration,
  totalCost,
  invoiceURL,
  status,
  fromTime,
  onChangeSessionBookingStatus,
  onEditSessionBooking,
}) => {
  const { appState } = useContext(AppContext);
  const userRole = appState?.userRole;
  return (
    <SimpleCard className="rounded">
      <FlexBox justifyContent="center" alignItems="start" direction="column">

        <FlexBox justifyContent="between" alignItems="center">
          <small>Clinic:</small>
          <small>
            <strong>{clinicName}</strong>
          </small>
        </FlexBox>
        
        {(userRole === 'admin' || userRole === 'receptionist') && (
          <FlexBox justifyContent="between" alignItems="center">
            <small>Patient:</small>
            <small>
              <strong>{patientName}</strong>
            </small>
          </FlexBox>
        )}

        <FlexBox justifyContent="between" alignItems="center">
          <small>Therapy:</small>
          <small>
            <strong>{therapyName}</strong>
          </small>
        </FlexBox>

        <FlexBox justifyContent="between" alignItems="center">
          <small>Date:</small>
          <small>
            <strong>{formatDateUTCwithoutDay(fromTime)}</strong>
          </small>
        </FlexBox>

        <FlexBox justifyContent="between" alignItems="center">
          <small>From:</small>
          <small>
            <strong>{toFormattedDateUTC(new Date(fromTime), "hh:mm a")}</strong>
          </small>
        </FlexBox>
        <FlexBox justifyContent="between" alignItems="center">
          <small>Duration:</small>
          <small>
            <strong>{duration} mins</strong>
          </small>
        </FlexBox>
        {status && (
          <FlexBox justifyContent="between" alignItems="center">
            <small>Payment Status:</small>
            <small>
              <strong>{status}</strong>
            </small>
          </FlexBox>
        )}
        {invoiceURL && (
          <FlexBox justifyContent="between" alignItems="center">
            <small>Invoice:</small>
            <small>
              <a
                href={invoiceURL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline"
              >
                View Invoice
              </a>
            </small>
          </FlexBox>
        )}

        <hr />
        <FlexBox justifyContent="between" alignItems="center">
          <small>
            <b>Total Cost: </b>
          </small>
          <small>
            <strong>
              {GBP_SYMBOL}&nbsp;
              {totalCost}
            </strong>
          </small>
        </FlexBox>
        <hr />
        <FlexBox gap="2">
          {!['completed', 'cancelled'].includes(status) && ( // userRole === 'admin' && 
            <>
            <SimpleButton onClick={() => onChangeSessionBookingStatus?.(id, 'cancelled')}>
              Cancel
            </SimpleButton>
            {(userRole === 'admin' || userRole === 'receptionist') && (
            <SimpleButton variant="success" onClick={() => onChangeSessionBookingStatus?.(id, 'completed')}>
              Complete
            </SimpleButton>
            )}
            </>
          )}
        </FlexBox>
        {userRole === 'admin' && (
          <>
          <hr/>
          <FlexBox justifyContent="center" gap="2">
            <SimpleButton variant="primary" onClick={() => onEditSessionBooking?.(id)}>
              Edit Session Booking
            </SimpleButton>
          </FlexBox>
          </>
        )}
      </FlexBox>
    </SimpleCard>
  );
};

export default PatientSessionBookingSummaryTile;
